import React from "react";
import { ChevronDown, X } from "lucide-react";

// Known postal codes for major Romanian cities (first match per city name)
const POSTAL = {
  // București
  "Sector 1": "010001", "Sector 2": "020001", "Sector 3": "030001",
  "Sector 4": "040001", "Sector 5": "050001", "Sector 6": "060001",
  // Alba
  "Alba Iulia": "510001", "Aiud": "511400", "Blaj": "515400", "Sebeș": "515800",
  // Arad
  "Arad": "310001", "Nădlac": "315500", "Curtici": "315100",
  // Argeș
  "Pitești": "110001", "Câmpulung": "115100", "Curtea de Argeș": "115300", "Mioveni": "115300",
  // Bacău
  "Bacău": "600001", "Onești": "601001", "Moinești": "605400", "Buhuși": "605100",
  // Bihor
  "Oradea": "410001", "Salonta": "415500", "Beiuș": "415200", "Marghita": "415300",
  // Bistrița-Năsăud
  "Bistrița": "420001", "Năsăud": "425200", "Beclean": "425100",
  // Botoșani
  "Botoșani": "710001", "Dorohoi": "715200",
  // Brăila
  "Brăila": "810001",
  // Brașov
  "Brașov": "500001", "Făgăraș": "505200", "Săcele": "505600", "Codlea": "505100",
  "Râșnov": "505400", "Zărnești": "505800", "Predeal": "505300",
  // Buzău
  "Buzău": "120001", "Râmnicu Sărat": "125300",
  // Caraș-Severin
  "Reșița": "320001", "Caransebeș": "325400",
  // Călărași
  "Călărași": "910001", "Oltenița": "915400",
  // Cluj
  "Cluj-Napoca": "400001", "Turda": "401600", "Dej": "405200", "Gherla": "405300",
  "Câmpia Turzii": "401800", "Florești": "407280",
  // Constanța
  "Constanța": "900001", "Mangalia": "905500", "Medgidia": "905600",
  "Năvodari": "905700", "Ovidiu": "907300", "Cernavodă": "905200",
  // Covasna
  "Sfântu Gheorghe": "520001", "Târgu Secuiesc": "525400",
  // Dâmbovița
  "Târgoviște": "130001", "Moreni": "135400", "Pucioasa": "135500", "Găești": "135200",
  // Dolj
  "Craiova": "200001", "Băilești": "205100", "Calafat": "205200", "Filiași": "205400",
  // Galați
  "Galați": "800001", "Tecuci": "805300",
  // Giurgiu
  "Giurgiu": "080001",
  // Gorj
  "Târgu Jiu": "210001", "Motru": "215200", "Rovinari": "215400",
  // Harghita
  "Miercurea Ciuc": "530001", "Odorheiu Secuiesc": "535600", "Gheorgheni": "535500",
  // Hunedoara
  "Deva": "330001", "Hunedoara": "331001", "Petroșani": "332001",
  // Ialomița
  "Slobozia": "920001", "Urziceni": "925300", "Fetești": "925100",
  // Iași
  "Iași": "700001", "Pașcani": "705200",
  // Ilfov
  "Buftea": "070001", "Voluntari": "077190", "Popești-Leordeni": "077160",
  "Otopeni": "075100", "Pantelimon": "077145",
  // Maramureș
  "Baia Mare": "430001", "Sighetu Marmației": "435500", "Borșa": "435200",
  // Mehedinți
  "Drobeta-Turnu Severin": "220001", "Orșova": "225200",
  // Mureș
  "Târgu Mureș": "540001", "Reghin": "545300", "Sighișoara": "545400",
  // Neamț
  "Piatra-Neamț": "610001", "Roman": "611500",
  // Olt
  "Slatina": "230001", "Caracal": "235200",
  // Prahova
  "Ploiești": "100001", "Câmpina": "105600", "Sinaia": "106100", "Bușteni": "105500",
  "Breaza": "105300",
  // Satu Mare
  "Satu Mare": "440001", "Carei": "445100",
  // Sălaj
  "Zalău": "450001",
  // Sibiu
  "Sibiu": "550001", "Mediaș": "551001", "Cisnădie": "555300",
  // Suceava
  "Suceava": "720001", "Rădăuți": "725400", "Fălticeni": "725100",
  "Câmpulung Moldovenesc": "725100", "Vatra Dornei": "725700", "Gura Humorului": "725300",
  // Teleorman
  "Alexandria": "140001", "Roșiorii de Vede": "145100", "Turnu Măgurele": "145200",
  // Timiș
  "Timișoara": "300001", "Lugoj": "305500", "Sânnicolau Mare": "305600",
  "Jimbolia": "305400", "Dumbrăvița": "307160",
  // Tulcea
  "Tulcea": "820001",
  // Vaslui
  "Vaslui": "730001", "Bârlad": "731001", "Huși": "735100",
  // Vâlcea
  "Râmnicu Vâlcea": "240001", "Drăgășani": "245500",
  // Vrancea
  "Focșani": "620001", "Adjud": "625100",
};

// Strip Romanian diacritics → ASCII for search comparison
function normSearch(s) {
    return s.toLowerCase()
        .replace(/[ăâ]/g, "a")
        .replace(/[î]/g, "i")
        .replace(/[șş]/g, "s")
        .replace(/[țţ]/g, "t");
}

export default function CityCombobox({ cities = [], value, onChange, onPostalCode, placeholder = "Localitate", required, className = "" }) {
    const [open, setOpen] = React.useState(false);
    const [q, setQ] = React.useState(value || "");
    const wrapRef = React.useRef(null);

    // Sync q when value changes externally (e.g. form reset)
    React.useEffect(() => { setQ(value || ""); }, [value]);

    // Close on outside click
    React.useEffect(() => {
        const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const filtered = React.useMemo(() => {
        if (!q) return cities.slice(0, 30);
        const qn = normSearch(q);
        return cities.filter(c => normSearch(c).includes(qn)).slice(0, 50);
    }, [q, cities]);

    const select = (city) => {
        setQ(city);
        onChange(city);
        setOpen(false);
        if (onPostalCode && POSTAL[city]) onPostalCode(POSTAL[city]);
    };

    const handleInput = (e) => {
        setQ(e.target.value);
        onChange(e.target.value);
        setOpen(true);
    };

    const clear = () => { setQ(""); onChange(""); if (onPostalCode) onPostalCode(""); };

    return (
        <div ref={wrapRef} className={`relative ${className}`}>
            <div className="relative">
                <input
                    type="text"
                    value={q}
                    required={required}
                    placeholder={placeholder}
                    autoComplete="off"
                    onChange={handleInput}
                    onFocus={() => setOpen(true)}
                    className="input-soft w-full pr-16"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                    {q && (
                        <button type="button" onMouseDown={(e) => { e.preventDefault(); clear(); }}
                            className="px-1 text-muted-foreground hover:text-foreground">
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); setOpen(o => !o); }}
                        className="px-2 text-muted-foreground hover:text-foreground">
                        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                </div>
            </div>
            {open && filtered.length > 0 && (
                <div className="absolute z-50 left-0 right-0 top-full mt-0.5 bg-card border border-border shadow-xl max-h-52 overflow-y-auto rounded-b-lg">
                    {filtered.map(city => (
                        <button
                            key={city}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); select(city); }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-muted border-b border-border/30 last:border-0 flex items-center justify-between ${city === value ? "bg-primary/10 text-primary font-medium" : ""}`}
                        >
                            <span>{city}</span>
                            {POSTAL[city] && <span className="text-xs text-muted-foreground font-mono">{POSTAL[city]}</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
